import
{Injectable, Optional} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParameterCodec, HttpParams} from '@angular/common/http';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {
  AppNameEnum,
  CheckParams,
  ConverterOutput,
  EricResponse,
  FrontendDocument,
  SendRequest,
  Status,
  TaskData
} from '@nachdigal';
import {Configuration} from './configuration';
import {catchError, shareReplay} from 'rxjs/operators';
import {isEmpty} from 'lodash';
interface BehaviorSubjectDict<T>{
  [Id: string]: BehaviorSubject<T>;
}
@Injectable({
  providedIn: 'root'
})

export class NachdigalService {

  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();
  public encoder: HttpParameterCodec;

  private readonly NACHDIGAL = 'api/cnr/night-ingate/datev/v1/nachdigal/v1';
  private readonly NACHDIGAL_CONVERT = 'api/cnr/night-ingate/datev/v1/nachdigal/v2024';
  private readonly NIGHTINGATE_FRONTEND = 'api/cnr/night-ingate/datev/v1/frontend/v1';
  private readonly headerContentType = 'Content-Type';

  constructor(protected httpClient: HttpClient, @Optional() configuration: Configuration) {
    if (configuration) {
      this.configuration = configuration;
    }
    this.encoder = this.configuration.encoder;
  }

  private frontendDocumentCache: Observable<FrontendDocument>;
  private frontendIdCache;
  public getFrontendDocument(id: string, forceReload = false):  Observable<FrontendDocument> {
    if (this.frontendIdCache !== id || forceReload) {
      // console.log('load new', id);
      // TODO: compare share and shareReplay and choose the fitting rxjs method, maybe implement like attachmentservice without sharereplay
      this.frontendDocumentCache = this.getDocumentIds(id).pipe(shareReplay(1));
      this.frontendIdCache = id;
    } else {
      // console.log('return cache', id);
    }
    return this.frontendDocumentCache;
  }

  public getDocumentIds(id: string): Observable<FrontendDocument> {
    if (!id) {
      throw new Error('Required parameter id was null or undefined when calling documentsIdGet.');
    }
    return this.httpClient.get<FrontendDocument>(`${this.NIGHTINGATE_FRONTEND}/documents/${encodeURIComponent(String(id))}`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept',
          this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON, AcceptHeaderEnum.PDF]))
      }
    );
  }

  public getDocumentsMetadata(appName: AppNameEnum): Observable<Array<FrontendDocument>> {
    if (!appName) {
      throw new Error('Required parameter appName was null or undefined when calling documentsMetadataAppNameGet.');
    }
    return this.httpClient.get<Array<FrontendDocument>>(
      `${this.NIGHTINGATE_FRONTEND}/documents/metadata/${encodeURIComponent(String(appName))}`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept',
          this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]))
      }
    );
  }

  public getTaskData(nid: string): Observable<TaskData> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling getTaskData.');
    }
    return this.httpClient.get<TaskData>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/data`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]))
      }
    );
  }

  public postTaskData(nid: string, taskData: TaskData, clientName?: string): Observable<unknown> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling postTaskData.');
    }
    if (!taskData) {
      throw new Error('Required parameter taskData was null or undefined when calling postTaskData.');
    }
    const params = new HttpParams({encoder: this.encoder});
    const header = this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]);
        const contentType = this.configuration.selectHeaderContentType([AcceptHeaderEnum.JSON]);
    return this.httpClient.post<unknown>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/data`,
      taskData,
      {
        params,
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders
          .set('Accept', header)
          .set(this.headerContentType, contentType)
          .set('NACHDIGAL_CLIENT_NAME', clientName)
      }
    );
  }

  public getTaskStatus(nid: string): Observable<Status> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling getTaskStatus.');
    }
    return this.httpClient.get<Status>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/status`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]))
      }
    );
  }

  public sendTask(nid: string, sendRequest: SendRequest): Observable<Status> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling sendTask.');
    }
    if (!sendRequest) {
      throw new Error('Required parameter sendRequest was null or undefined when calling sendTask.');
    }
    const header = this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]);
    const contentType = this.configuration.selectHeaderContentType([AcceptHeaderEnum.JSON]);
    return this.httpClient.post<Status>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/send`,
      sendRequest,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header).set(this.headerContentType, contentType)
      }
    );
  }

  public getPreview(nid: string): Observable<Blob> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling getPreview.');
    }
    return this.httpClient.get(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/preview`,
      {
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', this.configuration.selectHeaderAccept([AcceptHeaderEnum.PDF]))
      }
    );
  }

  public postPreview(nid: string, dataVersion?: string, body?: string): Observable<Blob> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling postPreview.');
    }
    const params = new HttpParams({encoder: this.encoder}).append('data-version', dataVersion);
    const header = this.configuration.selectHeaderAccept([AcceptHeaderEnum.PDF, AcceptHeaderEnum.JSON]);
    const contentType = this.configuration.selectHeaderContentType([AcceptHeaderEnum.XML]);
    return this.httpClient.post(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/preview`,
      body,
      {
        params,
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header).set(this.headerContentType, contentType)
      }
    );
  }

  public getSingleFilePreview(taskId: string, docId): Observable<Blob> {
    if (!taskId || !docId) {
      throw new Error('Required parameter nid was null or undefined when calling getSingleFilePreview.');
    }
    return this.httpClient.get(`${this.NACHDIGAL}/task/${encodeURIComponent(String(taskId))}/document/${encodeURIComponent(String(docId))}/pdf`,
      {
        responseType: 'blob',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', this.configuration.selectHeaderAccept([AcceptHeaderEnum.PDF]))
      }
    );
  }

  public convertJsonToXml(nid: string, appName: AppNameEnum, body: string): Observable<ConverterOutput> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling convertJsonToXml.');
    }
    if (!appName) {
      throw new Error('Required parameter appName was null or undefined when calling convertJsonToXml.');
    }
    if (!body) {
      throw new Error('Required parameter body was null or undefined when calling convertJsonToXml.');
    }
    const header = this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]);
    const contentType = this.configuration.selectHeaderContentType([AcceptHeaderEnum.JSON]);
    return this.httpClient.post<ConverterOutput>(
      `${this.NACHDIGAL_CONVERT}/task/${encodeURIComponent(String(nid))}/convert/${encodeURIComponent(String(appName))}`,
      body,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header).set(this.headerContentType, contentType)
      }
    );
  }

  public validateXml(nid: string, checkParams: CheckParams): Observable<EricResponse> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling validateXml.');
    }
    if (!checkParams) {
      throw new Error('Required parameter checkParams was null or undefined when calling validateXml.');
    }
    const header = this.configuration.selectHeaderAccept([AcceptHeaderEnum.JSON]);
    const contentType = this.configuration.selectHeaderContentType([AcceptHeaderEnum.JSON]);
    return this.httpClient.post<EricResponse>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/check`,
      checkParams,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header).set(this.headerContentType, contentType)
      }
    );
  }

  public delete(nid: string): Observable<unknown> {
    if (!nid) {
      throw new Error('Required parameter nid was null or undefined when calling delete.');
    }
    return this.httpClient.delete(`${this.NACHDIGAL}/task/${nid}`);
  }



  public postAttachment(nid: string, data: any, params: AttachmentParams){
    const header = this.configuration.selectHeaderAccept([ AcceptHeaderEnum.JSON]);
    const contentType = this.configuration.selectHeaderContentType([params['Content-Type']]);
    const docName = encodeURIComponent(String(params['Doc-Name']));
    return this.httpClient.post<Attachment>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/document`,
      data,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header).set(this.headerContentType, contentType)
          .set('Doc-Source-Id',params['Doc-Source-Id']).set('Doc-Source',params['Doc-Source']).set('Doc-Name',docName)
          .set('Doc-Name-Encoding',params['Doc-Name-Encoding'])
      }
    );
  }

  public deleteAttachment(nid: string, did: string){
    const header = this.configuration.selectHeaderAccept([ AcceptHeaderEnum.JSON]);
    return this.httpClient.delete<null>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}/document/${did}`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header)
      }
    );
  }

  public getAttachments(nid: string){
    const header = this.configuration.selectHeaderAccept([ AcceptHeaderEnum.JSON]);
    return this.httpClient.get<AttachmentsList>(`${this.NACHDIGAL}/task/${encodeURIComponent(String(nid))}`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header)
      }
    );
  }

  public getUserEmail(taskId: string){
    const header = this.configuration.selectHeaderAccept([ AcceptHeaderEnum.JSON]);
    return this.httpClient.get<{ attachments_email: string}>(`${this.NACHDIGAL}/task/${taskId}/callbackinfo`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header)
      }
    );
  }

  public saveUserEmail(taskId: string, userMail: string){
    const body = {
     attachments_email: isEmpty(userMail) ? '' : userMail
    };
    const header = this.configuration.selectHeaderAccept([ AcceptHeaderEnum.JSON]);
    return this.httpClient.post<{ attachments_email: string}>(`${this.NACHDIGAL}/task/${taskId}/callbackinfo`,
      body,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header)
      }
    );
  }

  public getAttachmentConfig() {
    const header = this.configuration.selectHeaderAccept([ AcceptHeaderEnum.JSON]);
    return this.httpClient.get<AttachmentConfig>(`${this.NACHDIGAL}/configuration`,
      {
        responseType: 'json',
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders.set('Accept', header)
      }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return of(null);
      })
    );
  }
}

export interface AttachmentParams{
  'Content-Type': string;
  'Doc-Name-Encoding': string;
  'Doc-Name': string;
  'Doc-Source-Id': string;
  'Doc-Source': string;
}
export interface Attachment {
  doc_id: string;
  doc_name: string;
  doc_source_id: string;
  doc_source: string;
  doc_size: number;
}
export interface AttachmentsList{
  creation_date: string;
  documents: Attachment[];
  elements_of_order: any;
  genuine: boolean;
  last_status_update: string;
  nid: string;
  status: string;
}

export interface AttachmentConfig{
  document_count: number;
  document_size: number;
  all_documents_size: number;
  document_name_length: number;
  document_max_page_count: number;
}

export const AcceptHeaderEnum = {
  JSON: 'application/json',
  PDF: 'application/pdf',
  XML: 'application/xml'
};
